import React from "react";
import { Link } from "react-router-dom";
import { pdf } from "@react-pdf/renderer";
import {
  ArrowLeft, Download, MapPin, Phone, Mail, Linkedin, Globe,
  User, Sparkles, Briefcase, Target, GraduationCap, Award, Globe2, Languages,
} from "lucide-react";
import ResumeDocument from "./ResumeDocument";
import profile from "../../data/profile.json";
import "../portfolio/styles.css";

const FILENAME = "Roshan-Raykar-Resume.pdf";

/* ---------- palette (mirrors PDF) ---------- */
const C = {
  bg:     "#EDE7D9",
  paper:  "#F6F1E5",
  ink:    "#262016",
  body:   "#3D3428",
  muted:  "#6B5F4F",
  faint:  "#8A7E6B",
  rule:   "#D6CCB4",
  wood:   "#B5793F",
  mint:   "#23C795",
  blue:   "#19B5DE",
};

/* Impact tile accent map — mirrors home Impact block */
const TILE_ACCENT = { wood: "#B5793F", mint: "#23C795", blue: "#19B5DE", ink: "#262016" };
const TILE_TINT = {
  wood: "linear-gradient(135deg, rgba(181,121,63,0.10), rgba(181,121,63,0.02))",
  mint: "linear-gradient(135deg, rgba(35,199,149,0.10), rgba(35,199,149,0.02))",
  blue: "linear-gradient(135deg, rgba(25,181,222,0.10), rgba(25,181,222,0.02))",
  ink:  "linear-gradient(135deg, rgba(38,32,22,0.08),  rgba(38,32,22,0.02))",
};
const TILE_SPAN = {
  small:   "md:col-span-3",
  regular: "md:col-span-4",
  wide:    "md:col-span-6",
};

/* ---------- helpers ---------- */
const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
function fmtDate(iso) {
  if (!iso) return "";
  const m = /^(\d{4})-(\d{2})/.exec(iso);
  if (!m) return iso;
  return `${MONTHS[parseInt(m[2], 10) - 1]} ${m[1]}`;
}
function renderMixed(text) {
  if (!text) return null;
  const parts = String(text).split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) =>
    p.startsWith("**") && p.endsWith("**") ? (
      <strong key={i} className="font-semibold text-[#262016]">{p.slice(2, -2)}</strong>
    ) : (
      <React.Fragment key={i}>{p}</React.Fragment>
    )
  );
}
async function downloadPdf() {
  const blob = await pdf(<ResumeDocument />).toBlob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = FILENAME;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

/* ---------- primitives ---------- */
const SectionHeading = ({ children, color = C.wood, icon: Icon, testId }) => (
  <div className="flex items-center gap-3 mb-6" data-testid={testId}>
    <span className="block" style={{ width: 36, height: 3.5, background: color, borderRadius: 2 }} />
    {Icon && <Icon size={18} strokeWidth={1.75} style={{ color }} />}
    <h2
      className="text-[1.05rem] md:text-[1.2rem] font-bold uppercase text-[#262016]"
      style={{ fontFamily: "'Space Grotesk', sans-serif", letterSpacing: "0.06em" }}
    >
      {children}
    </h2>
  </div>
);

const Bullet = ({ children }) => (
  <li className="flex items-start gap-2.5">
    <span className="mt-[3px] shrink-0 font-semibold text-[#B5793F]"
          style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "0.95rem" }}>›</span>
    <span
      className="text-[13.5px] leading-[1.65]"
      style={{ color: C.body, textWrap: "pretty" }}
    >
      {children}
    </span>
  </li>
);

/** Section partition — used between major blocks */
const SectionDivider = () => (
  <div className="mt-10 mb-6 flex items-center gap-3">
    <span className="flex-1 h-px" style={{ background: C.rule }} />
    <span className="w-1.5 h-1.5 rounded-full" style={{ background: C.wood }} />
    <span className="flex-1 h-px" style={{ background: C.rule }} />
  </div>
);
/** Hairline used inside the header */
const HairRule2 = () => (
  <div className="h-px w-full mt-5" style={{ background: C.rule }} />
);

/* Render **bold** markers inside plain text */
const renderTileText = (text) => {
  if (!text) return null;
  const parts = String(text).split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) =>
    p.startsWith("**") && p.endsWith("**") ? (
      <strong key={i} className="font-semibold text-[#262016]">{p.slice(2, -2)}</strong>
    ) : (
      <React.Fragment key={i}>{p}</React.Fragment>
    )
  );
};

/* Home-style Impact tile — mirrors portfolio Impact block */
const ImpactTile = ({ tile }) => {
  const accent = TILE_ACCENT[tile.accent] || TILE_ACCENT.wood;
  const isLink = Boolean(tile.href);
  const isExternal = isLink && /^https?:\/\//.test(tile.href);
  const Wrapper = isLink ? "a" : "div";
  const wrapperProps = isLink
    ? {
        href: tile.href,
        ...(isExternal ? { target: "_blank", rel: "noreferrer noopener" } : {}),
      }
    : {};
  return (
    <Wrapper
      {...wrapperProps}
      className={`relative overflow-hidden col-span-1 ${TILE_SPAN[tile.size] || TILE_SPAN.regular} flex flex-col ${isLink ? "cursor-pointer transition-transform hover:-translate-y-0.5" : ""}`}
      style={{
        background: TILE_TINT[tile.accent] || TILE_TINT.wood,
        borderRadius: 14,
        padding: "20px 22px",
        border: `1px solid ${C.rule}`,
        minHeight: tile.size === "small" ? 140 : 180,
      }}
      data-testid={`resume-impact-tile-${tile.id}`}
    >
      <span className="absolute top-0 left-5 h-1 w-10 rounded-b-full" style={{ background: accent }} />
      <p
        className="mt-1 mb-2 text-[10px] uppercase tracking-[0.18em] font-semibold"
        style={{ color: accent, fontFamily: "'Space Grotesk', sans-serif" }}
      >
        {tile.eyebrow}
      </p>
      {tile.pair ? (
        <div className="grid grid-cols-2 gap-3">
          {tile.pair.map((p, i) => (
            <div key={i}>
              <p
                className="font-medium text-[#262016]"
                style={{
                  fontFamily: "'Fraunces', serif",
                  fontSize: "clamp(1.4rem, 2vw, 1.7rem)",
                  color: accent,
                  lineHeight: 1.1,
                }}
              >
                {p.big}
              </p>
              <p className="text-[12px] text-[#5C5042] mt-1" style={{ textWrap: "pretty" }}>{p.label}</p>
            </div>
          ))}
        </div>
      ) : (
        <>
          <p
            className="font-medium leading-[1.1] mb-1.5"
            style={{
              fontFamily: "'Fraunces', serif",
              fontSize: tile.size === "small"
                ? "clamp(1.5rem, 2.3vw, 1.85rem)"
                : "clamp(1.2rem, 1.75vw, 1.5rem)",
              color: tile.size === "small" ? accent : C.ink,
              textWrap: "balance",
            }}
          >
            {tile.big}
          </p>
          <p className="text-[12.5px] leading-[1.5] text-[#5C5042]" style={{ textWrap: "pretty" }}>
            {renderTileText(tile.text)}
          </p>
        </>
      )}
    </Wrapper>
  );
};

/* ---------- page ---------- */
export default function ResumePage() {
  const ident = profile.identity || {};
  const override = profile.resumeOverrides || {};

  return (
    <div
      className="rp-root min-h-screen relative"
      style={{ background: C.bg }}
      data-testid="resume-page"
    >
      <div className="rp-grain" />

      {/* Top bar */}
      <header
        className="sticky top-0 z-30 backdrop-blur"
        style={{
          background: "rgba(237,231,217,0.85)",
          borderBottom: `1px solid ${C.rule}`,
        }}
      >
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
          <Link
            to="/"
            data-testid="resume-back-link"
            className="inline-flex items-center gap-2 text-sm hover:text-[#262016] transition-colors"
            style={{ color: C.muted, fontFamily: "'Space Grotesk', sans-serif" }}
          >
            <ArrowLeft size={15} />
            Back to portfolio
          </Link>
          <button
            type="button"
            onClick={downloadPdf}
            data-testid="resume-download-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium hover:opacity-90 transition-all"
            style={{
              background: C.ink,
              color: C.bg,
              boxShadow: "0 6px 16px -8px rgba(38,32,22,0.5)",
              fontFamily: "'Space Grotesk', sans-serif",
            }}
          >
            <Download size={15} />
            Download PDF
          </button>
        </div>
      </header>

      {/* Resume paper */}
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-10 md:py-14">
        <article
          className="mx-auto"
          data-testid="resume-paper"
          style={{
            background: C.paper,
            border: `1px solid ${C.rule}`,
            borderRadius: 6,
            boxShadow: "0 30px 60px -30px rgba(38,32,22,0.35), 0 4px 12px rgba(38,32,22,0.08)",
            padding: "56px clamp(28px, 6vw, 64px) 56px",
            fontFamily: "'Space Grotesk', sans-serif",
          }}
        >
          {/* ---------- Header ---------- */}
          <header data-testid="resume-header">
            <h1
              className="leading-[0.98] tracking-tight text-[#262016]"
              style={{
                fontFamily: "'Fraunces', serif",
                fontWeight: 500,
                fontSize: "clamp(2.6rem, 5.2vw, 3.6rem)",
              }}
            >
              {ident.name}
            </h1>

            {/* Three-line tagline: headline → subline → signature */}
            <p
              className="mt-3"
              style={{
                fontFamily: "'Fraunces', serif",
                fontStyle: "italic",
                fontWeight: 500,
                fontSize: "clamp(1.2rem, 1.75vw, 1.45rem)",
                color: C.ink,
                lineHeight: 1.3,
                textWrap: "balance",
              }}
              data-testid="resume-tagline-headline"
            >
              {profile.identity?.headline || "Driving business growth with the power of design thinking and AI."}
            </p>
            <p
              className="mt-1.5"
              style={{
                fontFamily: "'Space Grotesk', sans-serif",
                fontSize: "0.95rem",
                color: C.muted,
                fontWeight: 500,
                lineHeight: 1.4,
              }}
              data-testid="resume-tagline-sub"
            >
              15 years across business, corporate, design and AI.
            </p>
            <p
              className="mt-1.5"
              style={{
                fontFamily: "'Fraunces', serif",
                fontStyle: "italic",
                fontWeight: 500,
                fontSize: "clamp(1.05rem, 1.5vw, 1.2rem)",
                color: C.wood,
                lineHeight: 1.3,
              }}
              data-testid="resume-tagline-signature"
            >
              Your go-to human in the loop.
            </p>

            {/* Contact */}
            <div
              className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[13px]"
              style={{ color: C.muted }}
              data-testid="resume-contact"
            >
              {ident.location && (
                <span className="inline-flex items-center gap-1.5">
                  <MapPin size={12} className="text-[#B5793F]" /> {ident.location}
                </span>
              )}
              {ident.email && (
                <>
                  <span className="text-[#B5793F]">·</span>
                  <a href={`mailto:${ident.email}`} className="inline-flex items-center gap-1.5 hover:text-[#262016]">
                    <Mail size={12} className="text-[#B5793F]" /> {ident.email}
                  </a>
                </>
              )}
              {ident.phone && (
                <>
                  <span className="text-[#B5793F]">·</span>
                  <a href={`tel:${ident.phone.replace(/\s/g,"")}`} className="inline-flex items-center gap-1.5 hover:text-[#262016]">
                    <Phone size={12} className="text-[#B5793F]" /> {ident.phone}
                  </a>
                </>
              )}
              {ident.linkedin && (
                <>
                  <span className="text-[#B5793F]">·</span>
                  <a href={ident.linkedin} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 hover:text-[#262016]">
                    <Linkedin size={12} className="text-[#B5793F]" />
                    {ident.linkedin.replace(/^https?:\/\/(www\.)?linkedin\.com\/in\//, "").replace(/\/$/, "")}
                  </a>
                </>
              )}
              {ident.portfolio && (
                <>
                  <span className="text-[#B5793F]">·</span>
                  <a href={ident.portfolio} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 hover:text-[#262016]">
                    <Globe size={12} className="text-[#B5793F]" />
                    <span className="uppercase" style={{ letterSpacing: "0.12em", color: C.muted, fontWeight: 600, fontSize: "0.7rem" }}>Portfolio:</span>
                    <span className="font-semibold">{"www." + ident.portfolio.replace(/^https?:\/\/(www\.)?/, "")}</span>
                  </a>
                </>
              )}
            </div>
            <HairRule2 />
          </header>

          {/* ---------- Summary ---------- */}
          <section data-testid="resume-summary" className="mt-4">
            <SectionHeading color={C.wood} icon={User}>Summary</SectionHeading>
            <p
              className="text-[14px] leading-[1.7]"
              style={{ color: C.body, textWrap: "pretty" }}
            >
              {override.summary || profile.summary}
            </p>
          </section>

          {/* ---------- Impact (matches home page block) ---------- */}
          {(profile.impactTiles || []).length > 0 && (
            <>
              <SectionDivider />
              <section data-testid="resume-impact" className="mt-4">
                <SectionHeading color={C.blue} icon={Sparkles}>Impact</SectionHeading>
                <div className="grid grid-cols-1 md:grid-cols-12 gap-3.5 md:gap-4">
                  {profile.impactTiles.map((t) => (
                    <ImpactTile key={t.id} tile={t} />
                  ))}
                </div>
              </section>
            </>
          )}

          {/* ---------- Experience ---------- */}
          <SectionDivider />
          <section data-testid="resume-experience" className="mt-4">
            <SectionHeading color={C.wood} icon={Briefcase}>Experience</SectionHeading>
            <div className="space-y-16">
              {(profile.experience || []).map((org, oi) => (
                <div
                  key={oi}
                  style={{
                    breakInside: "avoid",
                    paddingTop: oi === 0 ? 0 : 24,
                    borderTop: oi === 0 ? "none" : `1px solid ${C.rule}`,
                  }}
                >
                  <h3
                    className="leading-[1.1] text-[#262016] mb-4"
                    style={{
                      fontFamily: "'Fraunces', serif",
                      fontWeight: 600,
                      fontSize: "clamp(1.35rem, 2vw, 1.7rem)",
                    }}
                  >
                    {org.org}
                  </h3>

                  <div>
                    {(org.roles || []).map((r, ri) => (
                      <div key={ri} style={{ marginTop: ri === 0 ? 0 : 40 }}>
                        <div className="flex items-baseline justify-between gap-4 mb-1">
                          <h4
                            className="text-[#262016]"
                            style={{
                              fontFamily: "'Fraunces', serif",
                              fontStyle: "italic",
                              fontWeight: 500,
                              fontSize: "clamp(1.05rem, 1.4vw, 1.2rem)",
                              textWrap: "balance",
                            }}
                          >
                            {r.title}
                          </h4>
                          <span
                            className="shrink-0 whitespace-nowrap font-semibold"
                            style={{
                              fontFamily: "'Space Grotesk', sans-serif",
                              fontSize: "0.72rem",
                              textTransform: "uppercase",
                              color: C.faint,
                            }}
                          >
                            {fmtDate(r.startDate)} — {r.endDate ? fmtDate(r.endDate) : "Present"}
                          </span>
                        </div>
                        {(r.metrics || []).length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mb-2">
                            {r.metrics.map((m) => (
                              <span
                                key={m}
                                className="px-2 py-0.5 rounded-full text-[11px] text-[#262016]"
                                style={{
                                  background: "rgba(35,199,149,0.16)",
                                  fontFamily: "'Space Grotesk', sans-serif",
                                  fontWeight: 500,
                                }}
                              >
                                {m}
                              </span>
                            ))}
                          </div>
                        )}
                        <ul className="space-y-1.5 mt-1.5">
                          {(r.bullets || []).map((b, bi) => (
                            <Bullet key={bi}>{renderMixed(b)}</Bullet>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Selected case studies moved to portfolio site (see callout below) */}

          {/* ---------- Skills ---------- */}
          <SectionDivider />
          <section data-testid="resume-skills" className="mt-4">
            <SectionHeading color={C.wood} icon={Award}>Skills & Tools</SectionHeading>
            <div className="space-y-3">
              {(profile.skills?.coreCompetencies || []).length > 0 && (
                <p className="text-[13.5px] leading-[1.7]" style={{ color: C.body, textWrap: "pretty" }}>
                  <span className="font-semibold text-[#262016]">Core competencies: </span>
                  {profile.skills.coreCompetencies.join(" · ")}
                </p>
              )}
              <p className="text-[13.5px] leading-[1.7]" style={{ color: C.body, textWrap: "pretty" }}>
                <span className="font-semibold text-[#262016]">Tools / Software / Platforms: </span>
                Adobe Creative Suite · Adobe Photoshop · Canva · Google Flow (Nano Banana + Omni) · Adobe Premiere Pro · Vyond · ChatGPT · Claude · Adobe AI · Gemini Enterprise · HeyGen · Synthesia · Adobe After Effects · Adobe Illustrator · 3DS Max · Unreal Engine · WordPress · Figma · Emergent · Gemini · MidJourney · Runway · Seedance · ElevenLabs
              </p>
            </div>
          </section>

          {/* ---------- Education ---------- */}
          <SectionDivider />
          <section data-testid="resume-education" className="mt-4">
            <SectionHeading color={C.wood} icon={GraduationCap}>Education</SectionHeading>
            <div>
              {(profile.education || []).map((e, i) => (
                <div key={i} style={{ marginTop: i === 0 ? 0 : 40 }}>
                  <div className="flex items-baseline justify-between gap-4">
                    <h4
                      className="text-[#262016]"
                      style={{
                        fontFamily: "'Fraunces', serif",
                        fontWeight: 500,
                        fontSize: "1.15rem",
                        lineHeight: 1.2,
                      }}
                    >
                      {e.degree}
                    </h4>
                    <span
                      className="shrink-0 whitespace-nowrap font-semibold"
                      style={{
                        fontFamily: "'Space Grotesk', sans-serif",
                        fontSize: "0.72rem",
                        textTransform: "uppercase",
                        color: C.faint,
                      }}
                    >
                      {e.startYear ? `${e.startYear} — ${e.endYear || "Present"}` : e.endYear}
                    </span>
                  </div>
                  <p className="text-[13.5px] mt-1.5" style={{ color: C.muted }}>
                    {e.institute}
                    {e.affiliation ? ` · ${e.affiliation}` : ""}
                  </p>
                  {(e.awards || []).length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {e.awards.map((a) => (
                        <span
                          key={a}
                          className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[12px] font-semibold"
                          style={{
                            background: "rgba(181,121,63,0.14)",
                            color: C.wood,
                            border: "1px solid rgba(181,121,63,0.35)",
                            fontFamily: "'Space Grotesk', sans-serif",
                          }}
                        >
                          <Award size={11} strokeWidth={2} />
                          {a}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>

          {/* ---------- Certifications ---------- */}
          {(profile.certifications || []).length > 0 && (
            <>
              <SectionDivider />
              <section data-testid="resume-certifications" className="mt-4">
                <SectionHeading color={C.blue} icon={Award}>Certifications</SectionHeading>
                <ul className="space-y-2">
                  {profile.certifications.map((c, i) => (
                    <Bullet key={i}>
                      <strong className="font-semibold text-[#262016]">{c.title}</strong>
                      {" — " + c.issuer + (c.year ? ` (${c.year})` : "")}
                    </Bullet>
                  ))}
                </ul>
              </section>
            </>
          )}

          {/* Exposure & Social Impact — moved to portfolio site (see callout below) */}

          {/* Languages moved into header block */}

          {/* ---------- The rest lives on the portfolio (QR callout) ---------- */}
          <section
            className="mt-12 flex flex-col md:flex-row items-center md:items-stretch gap-6 p-6 md:p-8"
            style={{
              background: "#EFE5CE",
              borderRadius: 14,
              borderLeft: `4px solid ${C.wood}`,
            }}
            data-testid="resume-qr"
          >
            <div
              className="shrink-0 p-2.5"
              style={{
                background: C.paper,
                borderRadius: 10,
                border: `1px solid ${C.rule}`,
                boxShadow: "0 8px 24px -12px rgba(38,32,22,0.28)",
              }}
            >
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=260x260&margin=2&format=png&color=262016&bgcolor=F6F1E5&data=${encodeURIComponent(profile.identity.portfolio || "https://www.rosh.work")}`}
                alt="Scan to visit rosh.work"
                className="w-[130px] h-[130px] md:w-[140px] md:h-[140px] block"
              />
            </div>
            <div className="flex-1">
              <p
                className="uppercase font-bold mb-2 inline-flex items-center gap-2"
                style={{
                  fontFamily: "'Space Grotesk', sans-serif",
                  color: C.wood,
                  fontSize: "0.72rem",
                  letterSpacing: "0.22em",
                }}
              >
                <Sparkles size={13} strokeWidth={2} /> Full portfolio · Video resume · AI chat
              </p>
              <p
                className="text-[#262016]"
                style={{
                  fontFamily: "'Fraunces', serif",
                  fontStyle: "italic",
                  fontWeight: 500,
                  fontSize: "1.45rem",
                  lineHeight: 1.2,
                }}
              >
                The rest lives on my portfolio.
              </p>
              <p
                className="mt-2.5 text-[13.5px] leading-[1.65]"
                style={{ color: C.body, textWrap: "pretty" }}
              >
                Detailed case studies, design portfolio and social-impact work are documented in full at the link below:
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                {["Business Impact", "AI Readiness", "Design Portfolio", "Exposure & Social Impact"].map((chip) => (
                  <span
                    key={chip}
                    className="px-3 py-1 rounded-full text-[12px] font-semibold"
                    style={{
                      background: "rgba(181,121,63,0.14)",
                      color: C.wood,
                      border: "1px solid rgba(181,121,63,0.35)",
                      fontFamily: "'Space Grotesk', sans-serif",
                    }}
                  >
                    {chip}
                  </span>
                ))}
              </div>
              <p
                className="mt-4 inline-flex items-center gap-1.5"
                style={{
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontWeight: 700,
                  fontSize: "0.95rem",
                  color: C.wood,
                }}
              >
                <Globe size={14} className="text-[#B5793F]" />
                <span className="uppercase" style={{ letterSpacing: "0.14em", color: C.muted, fontWeight: 600, fontSize: "0.72rem" }}>Portfolio:</span>
                {"www." + (profile.identity.portfolio || "www.rosh.work").replace(/^https?:\/\/(www\.)?/, "")}
              </p>
            </div>
          </section>
        </article>

        {/* Footer download */}
        <div className="flex flex-col md:flex-row items-center justify-end gap-4 mt-10">
          <button
            type="button"
            onClick={downloadPdf}
            data-testid="resume-download-btn-bottom"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium hover:opacity-90 transition-all"
            style={{
              background: C.ink,
              color: C.bg,
              boxShadow: "0 6px 16px -8px rgba(38,32,22,0.5)",
              fontFamily: "'Space Grotesk', sans-serif",
            }}
          >
            <Download size={15} />
            Download PDF
          </button>
        </div>
      </div>
    </div>
  );
}

/** Programmatic downloader for any button elsewhere in the app. */
export async function downloadResumePdf() {
  return downloadPdf();
}
